import * as React from 'react';
import {
  Checkbox, DefaultButton, Dropdown, type IDropdownOption, Link, MessageBar, MessageBarType, PrimaryButton, ProgressIndicator, TextField
} from '@fluentui/react';
import styles from './SharePointOrganizer.module.scss';
import type { IAuditLog } from '../../../engine/AuditLog';
import type { IFileItem } from '../../../models/FileItem';
import type { IBatchResult } from '../../../models/Operation';
import type { IScanResult } from '../../../models/ScanResult';
import { getSystemFileSource } from '../../../rules/FilenameRule';
import { filePath, values } from '../../../rules/ruleUtils';
import {
  DOCUMENT_TYPES, type IAiTaggingService, type ILibraryMetadata, type ITagProposal, type ITagRequest, type ITagSet
} from '../../../tagging/Tagging';
import { TaggingEngine } from '../../../tagging/TaggingEngine';
import { knownNames } from '../../../tagging/knownNames';
import { describeGraphError } from '../../../utils/errors';
import { formatNumber } from '../../../utils/format';
import type { ITelemetry } from '../../../utils/telemetry';

export interface ITaggingHost {
  ai: IAiTaggingService;
  createMetadata: (driveId: string) => ILibraryMetadata;
}

export interface ITagsViewProps {
  scan: IScanResult;
  tagging: ITaggingHost;
  auditLog: IAuditLog;
  userName: string;
  allowChanges: boolean;
  telemetry: ITelemetry;
}

interface IRow {
  proposal: ITagProposal;
  approved: boolean;
}

const BATCH: number = 20;
/** Below this, AI suggestions are marked for a careful check. */
const LOW_CONFIDENCE: number = 0.5;
/** More than this at once gets slow to review; tag a folder at a time. */
const MAX_FILES: number = 200;

function plural(n: number, one: string, many: string): string {
  return `${formatNumber(n)} ${n === 1 ? one : many}`;
}

/**
 * AI tags — Document Type, Organization, Project, Year — written to library columns so one
 * file can be found by filtering or grouping, instead of being copied into several folders.
 */
export default function TagsView(props: ITagsViewProps): React.ReactElement {
  const { scan, tagging, auditLog, userName, allowChanges, telemetry } = props;
  const [scope, setScope] = React.useState<string>('');
  const [rows, setRows] = React.useState<{ [fileId: string]: IRow }>({});
  const [busy, setBusy] = React.useState<boolean>(false);
  const [progress, setProgress] = React.useState<{ done: number; total: number } | undefined>(undefined);
  const [message, setMessage] = React.useState<{ type: MessageBarType; text: string } | undefined>(undefined);
  const [result, setResult] = React.useState<IBatchResult | undefined>(undefined);

  React.useEffect(() => { setRows({}); setResult(undefined); setMessage(undefined); }, [scan]);

  const folderOptions: IDropdownOption[] = [{ key: '', text: `Whole library (${scan.library.name})` }].concat(
    values(scan.folders)
      .filter(f => f.id !== scan.rootId && f.totalFileCount > 0)
      .sort((a, b) => a.path.localeCompare(b.path))
      .map(f => ({ key: f.path, text: `${f.path} (${formatNumber(f.totalFileCount)})` }))
  );
  const inScope: IFileItem[] = values(scan.files)
    .filter(f => !getSystemFileSource(f) && (!scope || f.parentPath === scope || f.parentPath.indexOf(`${scope}/`) === 0))
    .sort((a, b) => filePath(a).localeCompare(filePath(b)));
  const tooMany: boolean = inScope.length > MAX_FILES;

  const suggest = (): void => {
    const requests: ITagRequest[] = inScope.map(f => ({ fileId: f.id, name: f.name, extension: f.extension, folderPath: f.parentPath }));
    setBusy(true);
    setMessage(undefined);
    setResult(undefined);
    let tagged: number = 0;
    let used: number = 0;
    // Names from earlier batches are added, so the same project is spelled the same way throughout.
    const known = knownNames(scan);
    const remember = (list: string[], value: string | undefined): void => {
      if (value && !list.some(x => x.toLowerCase() === value.toLowerCase())) { list.push(value); }
    };
    const run = (start: number): Promise<void> => {
      if (start >= requests.length) { return Promise.resolve(); }
      return tagging.ai.suggestTags(scan.library.id, requests.slice(start, start + BATCH), known).then(r => {
        for (const p of r.proposals) {
          remember(known.organizations, p.tags.organization);
          remember(known.projects, p.tags.project);
        }
        tagged += r.proposals.length;
        used += r.creditsUsed;
        setRows(prev => {
          const next: { [fileId: string]: IRow } = { ...prev };
          for (const p of r.proposals) {
            next[p.fileId] = { proposal: p, approved: false };
          }
          return next;
        });
        return run(start + BATCH);
      });
    };
    run(0)
      .then(() => {
        telemetry.event('tags.suggested', { libraryId: scan.library.id, files: requests.length, tagged, credits: used });
        setMessage({ type: MessageBarType.success, text: `AI suggested tags for ${plural(tagged, 'file', 'files')} using ${plural(used, 'credit', 'credits')}. Check them, adjust any, then approve.` });
      })
      .catch(error => setMessage({ type: MessageBarType.warning, text: describeGraphError(error) }))
      .then(() => setBusy(false), () => setBusy(false));
  };

  const edit = (fileId: string, change: Partial<ITagSet>): void => {
    setRows(prev => ({ ...prev, [fileId]: { ...prev[fileId], proposal: { ...prev[fileId].proposal, tags: { ...prev[fileId].proposal.tags, ...change } } } }));
  };

  const approvedRows: IRow[] = Object.keys(rows).map(id => rows[id]).filter(r => r.approved);

  const apply = (): void => {
    setBusy(true);
    setProgress({ done: 0, total: approvedRows.length });
    const engine: TaggingEngine = new TaggingEngine({ scan, metadata: tagging.createMetadata(scan.library.id), audit: auditLog, user: userName });
    engine.apply(approvedRows.map(r => r.proposal), (done, total) => setProgress({ done, total }))
      .then(r => {
        const count = (status: string): number => r.entries.filter(e => e.action === 'tag' && e.status === status).length;
        telemetry.event('tags.applied', { libraryId: scan.library.id, batchId: r.batchId, tagged: count('success'), skipped: count('skipped'), failed: count('failed') });
        setResult(r);
        setRows({});
      })
      .catch(error => setMessage({ type: MessageBarType.error, text: describeGraphError(error) }))
      .then(() => { setBusy(false); setProgress(undefined); }, () => { setBusy(false); setProgress(undefined); });
  };

  const tagCount = (status: string): number => (result ? result.entries.filter(e => e.action === 'tag' && e.status === status).length : 0);
  const rowIds: string[] = inScope.map(f => f.id).filter(id => rows[id]);

  return (
    <div className={styles.plan}>
      <h3 className={styles.sectionTitle}>Tags</h3>
      <p className={styles.issueListDescription}>
        AI suggests a <strong>Document Type</strong>, <strong>Organization</strong>, <strong>Project</strong> and <strong>Year</strong> for
        each file. Approved tags go into library columns (created if missing), so a contract can be found under its project and
        with all contracts — without keeping copies in several folders. Values someone set by hand are never replaced, and
        tagging can be undone from History.
      </p>
      {!allowChanges && (
        <MessageBar isMultiline>
          <strong>Recommend-only mode.</strong> You can review suggested tags; to write them, turn on <strong>Allow changes to this site</strong>.
        </MessageBar>
      )}

      <div className={styles.selectorRow}>
        <Dropdown
          className={styles.libraryDropdown}
          label="Files to tag"
          options={folderOptions}
          selectedKey={scope}
          disabled={busy}
          onChange={(_, o) => o && setScope(String(o.key))}
        />
        <DefaultButton
          text={busy && !progress ? 'Asking AI…' : `Suggest tags with AI (${plural(inScope.length, 'file', 'files')} · ${plural(inScope.length * tagging.ai.creditsPerFile, 'credit', 'credits')})`}
          iconProps={{ iconName: 'Tag' }}
          disabled={busy || inScope.length === 0 || tooMany}
          onClick={suggest}
        />
      </div>
      {tooMany && <MessageBar>That&apos;s {formatNumber(inScope.length)} files. Choose a folder with at most {MAX_FILES} files to review them comfortably.</MessageBar>}
      {message && <MessageBar messageBarType={message.type} isMultiline onDismiss={() => setMessage(undefined)}>{message.text}</MessageBar>}

      {progress && (
        <ProgressIndicator label="Writing tags…" description={`${formatNumber(progress.done)} of ${formatNumber(progress.total)} files`}
          percentComplete={progress.total ? progress.done / progress.total : undefined} />
      )}

      {result && (
        <MessageBar messageBarType={result.abortedReason || tagCount('failed') ? MessageBarType.warning : MessageBarType.success} isMultiline>
          {result.abortedReason ?? `Tagged ${plural(tagCount('success'), 'file', 'files')}`}
          {tagCount('skipped') > 0 && `; ${formatNumber(tagCount('skipped'))} already had tags`}
          {tagCount('failed') > 0 && `; ${formatNumber(tagCount('failed'))} failed`}.
          {' '}In the library, use <strong>Filter</strong> or <strong>Group by</strong> on the new columns — for example
          Group by <em>Document Type</em> to see every contract together. Batch <code>{result.batchId}</code> is in
          History and the <Link href={auditLog.viewUrl} target="_blank" rel="noreferrer">audit log</Link>.
        </MessageBar>
      )}

      {rowIds.length > 0 && (
        <>
          <div className={styles.planToolbar}>
            <span className={styles.planProgress}>{formatNumber(approvedRows.length)} of {formatNumber(rowIds.length)} approved</span>
            <DefaultButton text="Approve all" iconProps={{ iconName: 'CheckList' }} disabled={busy}
              onClick={() => setRows(prev => { const n = { ...prev }; for (const id of rowIds) { n[id] = { ...n[id], approved: true }; } return n; })} />
            <PrimaryButton text={`Write approved tags (${formatNumber(approvedRows.length)})`} iconProps={{ iconName: 'Play' }}
              disabled={busy || !allowChanges || approvedRows.length === 0} onClick={apply} />
          </div>
          <div className={styles.tagTableWrap}>
            <table className={styles.tagTable}>
              <thead>
                <tr><th>Approve</th><th>File</th><th>Document Type</th><th>Organization</th><th>Project</th><th>Year</th></tr>
              </thead>
              <tbody>
                {rowIds.map(id => {
                  const row: IRow = rows[id];
                  const t: ITagSet = row.proposal.tags;
                  return (
                    <tr key={id} title={row.proposal.reason}>
                      <td><Checkbox checked={row.approved} ariaLabel="Approve" onChange={(_, c) => setRows(prev => ({ ...prev, [id]: { ...prev[id], approved: !!c } }))} /></td>
                      <td className={styles.tagFile}>
                        {filePath(scan.files[id])}
                        <div className={styles.tagReason}>
                          {Math.round(row.proposal.confidence * 100)}%
                          {row.proposal.confidence < LOW_CONFIDENCE && <strong> · low confidence — check carefully</strong>}
                          {row.proposal.reason && ` · ${row.proposal.reason}`}
                        </div>
                      </td>
                      <td>
                        <Dropdown ariaLabel="Document Type" selectedKey={t.documentType ?? ''}
                          options={[{ key: '', text: '—' }].concat(DOCUMENT_TYPES.map(d => ({ key: d, text: d })))}
                          onChange={(_, o) => o && edit(id, { documentType: o.key ? String(o.key) : undefined })} />
                      </td>
                      <td><TextField ariaLabel="Organization" value={t.organization ?? ''} onChange={(_, v) => edit(id, { organization: v || undefined })} /></td>
                      <td><TextField ariaLabel="Project" value={t.project ?? ''} onChange={(_, v) => edit(id, { project: v || undefined })} /></td>
                      <td className={styles.tagYear}>
                        <TextField ariaLabel="Year" value={t.year ? String(t.year) : ''}
                          onChange={(_, v) => edit(id, { year: v && /^\d{4}$/.test(v) ? Number(v) : undefined })} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
